
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/utils/languageUtils';
import { Button } from '@/components/ui/button';
import { DollarSign, BarChart3, Shield, Globe } from 'lucide-react';

const FeatureCard: React.FC<{ 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  delay: number 
}> = ({ icon, title, description, delay }) => {
  return (
    <div 
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 transition-all duration-500 hover:bg-white/10 hover:transform hover:translate-y-[-5px] animate-on-scroll will-change-transform"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-3 text-white text-center">{title}</h3>
      <p className="text-gray-300 text-center">{description}</p>
    </div>
  );
};

const WhyJoinSection: React.FC = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-fast');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative bg-gradient-to-b from-indigo-950/30 to-gray-900">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-900/10 to-transparent"></div>
      <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll will-change-transform">
            {t('whyJoin.header')}
          </h2>
          
          <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll will-change-transform">
            {t('whyJoin.subtitle')}
          </p>
          
          <p className="text-lg text-gray-300 max-w-3xl mx-auto animate-on-scroll will-change-transform">
            {t('whyJoin.copy')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard 
            icon={<DollarSign size={32} className="text-purple-400" />}
            title={t('whyJoin.feature1.title')} 
            description={t('whyJoin.feature1.desc')} 
            delay={0}
          />
          <FeatureCard 
            icon={<BarChart3 size={32} className="text-purple-400" />}
            title={t('whyJoin.feature2.title')} 
            description={t('whyJoin.feature2.desc')} 
            delay={0}
          />
          <FeatureCard 
            icon={<Shield size={32} className="text-purple-400" />}
            title={t('whyJoin.feature3.title')} 
            description={t('whyJoin.feature3.desc')} 
            delay={0}
          />
          <FeatureCard 
            icon={<Globe size={32} className="text-purple-400" />}
            title={t('whyJoin.feature4.title')} 
            description={t('whyJoin.feature4.desc')} 
            delay={0}
          />
        </div>
        
        <div className="mt-16 text-center animate-on-scroll will-change-transform">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-8 py-6 rounded-full transition-all transform hover:scale-105 shadow-lg"
            asChild
          >
            <Link to={getLocalizedPath('/sign-up', language)}>
              {t('whyJoin.cta')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WhyJoinSection;
